import { useState } from 'react'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { X, Check } from 'lucide-react'

interface GeneratorDataSelectorProps {
  type: string
  label: string
  data: any[]
  selectedId?: string
  selectedIds?: string[]
  multiSelect?: boolean
  onSelect?: (id: string, data: any) => void
  onMultiSelect?: (selected: any[]) => void
  roleOptions?: string[]
}

export function GeneratorDataSelector({
  type,
  label,
  data = [],
  selectedId,
  selectedIds = [],
  multiSelect = false,
  onSelect,
  onMultiSelect,
  roleOptions
}: GeneratorDataSelectorProps) {
  const [selectedItems, setSelectedItems] = useState<Map<string, any>>(
    new Map(selectedIds.map(id => {
      const item = data?.find(d => d?.id === id)
      return item ? [id, { ...item, role: roleOptions?.[0] }] : null
    }).filter(Boolean) as Array<[string, any]>)
  )
  
  const [roles, setRoles] = useState<Map<string, string>>(new Map())
  
  const handleToggleItem = (item: any) => {
    const newSelected = new Map(selectedItems)
    
    if (newSelected.has(item.id)) {
      newSelected.delete(item.id)
      roles.delete(item.id)
    } else {
      newSelected.set(item.id, item)
      if (roleOptions && roleOptions.length > 0) {
        roles.set(item.id, roleOptions[0])
      }
    }
    
    setSelectedItems(newSelected)
    
    if (onMultiSelect) {
      const selectedArray = Array.from(newSelected.values()).map(item => ({
        ...item,
        role: roles.get(item.id)
      }))
      onMultiSelect(selectedArray)
    }
  }
  
  const handleRoleChange = (itemId: string, role: string) => {
    const newRoles = new Map(roles)
    newRoles.set(itemId, role)
    setRoles(newRoles)
    
    if (onMultiSelect) {
      const selectedArray = Array.from(selectedItems.values()).map(item => ({
        ...item,
        role: newRoles.get(item.id) || roleOptions?.[0]
      }))
      onMultiSelect(selectedArray)
    }
  }
  
  const getItemDisplayName = (item: any) => {
    switch (type) {
      case 'world':
        return item.result?.name || item.input?.worldName || '無題の世界'
      case 'character':
        return item.result?.basicInfo?.name || item.result?.name || '無名のキャラクター'
      case 'item':
        return item.result?.basicInfo?.name || item.result?.name || '無名のアイテム'
      case 'enemy':
        return item.result?.basicInfo?.name || item.result?.name || '無名のエネミー'
      case 'relationship':
        return item.result?.characters?.[0]?.name || '関係性'
      case 'name':
        return item.result?.names?.[0] || '名前'
      case 'plot':
        return item.result?.title || item.result?.基本設定?.タイトル || 'プロット'
      case 'title':
        return item.result?.titles?.[0]?.title || 'タイトル'
      case 'wikipedia':
        return item.result?.title || 'Wikipedia記事'
      case 'document':
        return item.result?.title || item.result?.基本情報?.タイトル || '文献'
      default:
        return '無題'
    }
  }
  
  const getItemDescription = (item: any) => {
    switch (type) {
      case 'world':
        return item.result?.genres?.join(', ') || ''
      case 'character':
        return item.result?.basicInfo?.race || item.result?.race || ''
      case 'item':
        return item.result?.basicInfo?.type || item.result?.type || ''
      case 'enemy':
        return item.result?.basicInfo?.type || item.result?.type || ''
      case 'relationship':
        return item.result?.relationship?.type || ''
      case 'plot':
        return item.result?.genre || item.result?.基本設定?.ジャンル || ''
      case 'title':
        return item.result?.genre || ''
      case 'wikipedia':
        return item.result?.category || ''
      case 'document':
        return item.result?.type || item.result?.基本情報?.文献タイプ || ''
      default:
        return ''
    }
  }
  
  if (!multiSelect) {
    return (
      <div>
        <Label htmlFor={`${type}-select`}>{label}</Label>
        <Select value={selectedId || 'none'} onValueChange={(value) => {
          const item = (data || []).find(d => d?.id === value)
          if (onSelect) {
            onSelect(value, item)
          }
        }}>
          <SelectTrigger id={`${type}-select`} className="mt-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">使用しない</SelectItem>
            {(data || []).map((item) => (
              <SelectItem key={item.id} value={item.id}>
                <div>
                  <div className="font-medium">{getItemDisplayName(item)}</div>
                  {getItemDescription(item) && (
                    <div className="text-xs text-gray-500">{getItemDescription(item)}</div>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        {selectedItems.size > 0 && (
          <Badge variant="secondary">
            {selectedItems.size}個選択中
          </Badge>
        )}
      </div>
      
      <ScrollArea className="h-[300px] border rounded-lg p-4">
        <div className="space-y-2">
          {(data || []).map((item) => {
            const isSelected = selectedItems.has(item.id)
            const itemRole = roles.get(item.id) || roleOptions?.[0]
            
            return (
              <div
                key={item.id}
                className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                  isSelected ? 'bg-blue-50 border-blue-300' : 'hover:bg-gray-50'
                }`}
                onClick={() => handleToggleItem(item)}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => {}}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-1"
                  />
                  
                  <div className="flex-1">
                    <div className="font-medium">{getItemDisplayName(item)}</div>
                    {getItemDescription(item) && (
                      <div className="text-sm text-gray-500">{getItemDescription(item)}</div>
                    )}
                    
                    {isSelected && roleOptions && roleOptions.length > 0 && (
                      <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                        <Label className="text-xs">役割</Label>
                        <Select
                          value={itemRole}
                          onValueChange={(value) => handleRoleChange(item.id, value)}
                        >
                          <SelectTrigger className="h-8 text-xs mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {roleOptions.map((role) => (
                              <SelectItem key={role} value={role}>
                                {getRoleLabel(role)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  
                  {isSelected && (
                    <Check className="w-5 h-5 text-blue-500 mt-1" />
                  )}
                </div>
              </div>
            )
          })}
          
          {data.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              まだ{label}が保存されていません
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

// 役割のラベルを取得
function getRoleLabel(role: string): string {
  const labels: Record<string, string> = {
    // キャラクター
    pc: 'PC（プレイヤーキャラクター）',
    npc: 'NPC（ノンプレイヤーキャラクター）',
    boss: 'ボス',
    enemy: 'エネミー',
    ally: '味方',
    
    // アイテム
    key_item: 'キーアイテム',
    reward: '報酬',
    plot_device: 'プロットデバイス',
    optional: 'オプション',
    
    // エネミー
    minion: '雑魚',
    encounter: '遭遇',
    guardian: 'ガーディアン',
    
    // 関係性
    central: '中心的',
    subplot: 'サブプロット',
    background: '背景',
    
    // プロット
    main: 'メイン',
    backstory: 'バックストーリー',
    hook: 'フック',
    
    // タイトル
    scenario: 'シナリオ',
    chapter: '章',
    location: '場所',
    item: 'アイテム',
    
    // Wikipedia
    lore: '伝承',
    history: '歴史',
    organization: '組織',
    technology: '技術',
    
    // 文献
    reference: '参考資料',
    handout: 'ハンドアウト',
    prophecy: '予言',
    document: '文書'
  }
  
  return labels[role] || role
}